import {
  ArrowLeftRight,
  ChevronRight,
  CircleCheck,
  Plus,
  Receipt,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useExpenseEditor } from '@/features/expenses/editor';
import { useActiveGroup, useGroupsStore } from '@/features/groups/store';
import { useBalances, useLedgerStore, useSettlementPlan } from '@/features/ledger/store';
import { useSettingsStore } from '@/features/settings/store';
import { formatRelativeDate } from '@/lib/date';
import {
  Avatar,
  AvatarStack,
  Button,
  Card,
  CardHeader,
  CategoryGlyph,
  EmptyState,
  Money,
  PageHeader,
  StatTile,
  balancePhrase,
} from '@/ui';
import { formatMoney, sum } from '@domain/money';
import type { MemberBalance } from '@domain/types';

export function DashboardPage() {
  const navigate = useNavigate();
  const openNew = useExpenseEditor((state) => state.openNew);

  const group = useActiveGroup();
  const groups = useGroupsStore((state) => state.groups);
  const showInactive = useSettingsStore((state) => state.showInactiveMembers);

  const expenses = useLedgerStore((state) => state.allExpenses);
  const members = useLedgerStore((state) => state.members);
  const settlements = useLedgerStore((state) => state.settlements);

  const balances = useBalances();
  const plan = useSettlementPlan();
  const currency = group?.currency ?? 'USD';

  const totalSpentMinor = useMemo(
    () => sum(expenses.map((expense) => expense.amountMinor)),
    [expenses]
  );
  const outstandingMinor = useMemo(
    () => sum(plan.map((suggestion) => suggestion.amountMinor)),
    [plan]
  );

  const visibleBalances = useMemo(
    () =>
      [...balances]
        .filter((balance) => showInactive || balance.isActive || balance.netMinor !== 0)
        .sort((a, b) => b.netMinor - a.netMinor),
    [balances, showInactive]
  );

  if (groups.length === 0) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <Card padded={false}>
          <EmptyState
            icon={Users}
            title="Create your first group"
            description="Groups keep balances separate — one for the flat, one for the road trip, one for the office lunch run."
            action={
              <Button icon={Plus} onClick={() => navigate('/groups?new=1')}>
                New group
              </Button>
            }
          />
        </Card>
      </>
    );
  }

  if (!group) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <Card padded={false}>
          <EmptyState
            icon={Users}
            title="No group selected"
            description="Pick a group to see its balances."
            action={<Button onClick={() => navigate('/groups')}>Choose a group</Button>}
          />
        </Card>
      </>
    );
  }

  const maxAbsNet = Math.max(1, ...visibleBalances.map((row) => Math.abs(row.netMinor)));
  const recent = expenses.slice(0, 6);

  return (
    <>
      <PageHeader
        eyebrow={group.name}
        title="Dashboard"
        actions={
          // The hero card repeats both actions, so phones skip the duplicates.
          <div className="hidden items-center gap-2 sm:flex">
            <Button variant="secondary" icon={ArrowLeftRight} onClick={() => navigate('/settle')}>
              Settle up
            </Button>
            <Button icon={Plus} onClick={openNew}>
              Add expense
            </Button>
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <section className="bg-brand relative flex flex-col overflow-hidden rounded-card p-6 text-white shadow-pop sm:p-7 lg:col-span-2">
          <span
            className="pointer-events-none absolute -right-16 -top-16 size-56 rounded-full bg-white/10"
            aria-hidden
          />
          <span
            className="pointer-events-none absolute -bottom-24 right-24 size-48 rounded-full bg-white/[0.06]"
            aria-hidden
          />

          <div className="relative flex items-start justify-between gap-4">
            <p className="text-[13px] font-medium text-white/75">Outstanding balance</p>
            {members.length > 0 && (
              <span className="rounded-full bg-white/15 p-1">
                <AvatarStack
                  people={members.map((member) => ({ id: member.id, name: member.name }))}
                  max={4}
                />
              </span>
            )}
          </div>

          <p className="tnum relative mt-2 text-4xl font-bold tracking-tight sm:text-5xl">
            {formatMoney(outstandingMinor, currency)}
          </p>
          <p className="relative mt-2 flex items-center gap-1.5 text-sm text-white/85">
            {outstandingMinor === 0 ? (
              <>
                <CircleCheck size={15} /> Everyone is settled up
              </>
            ) : (
              `${plan.length} payment${plan.length === 1 ? '' : 's'} would clear it`
            )}
          </p>

          <div className="relative mt-auto flex flex-wrap gap-2 pt-7">
            <button
              type="button"
              onClick={() => navigate('/settle')}
              className="inline-flex h-10 items-center gap-2 rounded-xl bg-white px-4 text-sm font-semibold text-[#4338ca] shadow-sm transition hover:bg-white/90 active:scale-[0.98]"
            >
              <ArrowLeftRight size={16} strokeWidth={2.3} />
              Settle up
            </button>
            <button
              type="button"
              onClick={openNew}
              className="inline-flex h-10 items-center gap-2 rounded-xl bg-white/15 px-4 text-sm font-semibold text-white ring-1 ring-inset ring-white/25 transition hover:bg-white/25 active:scale-[0.98]"
            >
              <Plus size={16} strokeWidth={2.3} />
              Add expense
            </button>
          </div>
        </section>

        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-1">
          <StatTile
            label="Total spent"
            icon={Wallet}
            value={formatMoney(totalSpentMinor, currency)}
            caption={`${expenses.length} expense${expenses.length === 1 ? '' : 's'}`}
          />
          <StatTile
            label="Members"
            icon={Users}
            tone="success"
            value={String(members.length)}
            caption={`${settlements.length} settlement${settlements.length === 1 ? '' : 's'} recorded`}
          />
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-5">
        <Card padded={false} className="lg:col-span-3">
          <CardHeader
            className="mb-0 px-5 pb-3 pt-5"
            title="Balances"
            description="Positive means the group owes them."
            action={
              <Button variant="ghost" size="sm" icon={UserPlus} onClick={() => navigate('/members')}>
                Members
              </Button>
            }
          />

          {visibleBalances.length === 0 ? (
            <EmptyState
              icon={UserPlus}
              title="No members yet"
              description="Add the people who share expenses in this group."
              action={<Button onClick={() => navigate('/members')}>Add members</Button>}
            />
          ) : (
            <ul className="px-2 pb-2">
              {visibleBalances.map((row) => (
                <li key={row.memberId}>
                  <BalanceRow
                    row={row}
                    currency={currency}
                    share={Math.abs(row.netMinor) / maxAbsNet}
                    onClick={() => navigate(`/members/${row.memberId}`)}
                  />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card padded={false} className="lg:col-span-2">
          <CardHeader
            className="mb-0 px-5 pb-3 pt-5"
            title="Recent activity"
            action={
              <Button
                variant="ghost"
                size="sm"
                iconAfter={ChevronRight}
                onClick={() => navigate('/expenses')}
              >
                See all
              </Button>
            }
          />

          {recent.length === 0 ? (
            <EmptyState
              icon={Receipt}
              title="No expenses yet"
              description="Record what people spend and balances update automatically."
              action={
                <Button icon={Plus} onClick={openNew}>
                  Add expense
                </Button>
              }
            />
          ) : (
            <ul className="px-2 pb-2">
              {recent.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/expenses/${row.id}`)}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-elevated"
                  >
                    <CategoryGlyph icon={row.categoryIcon} color={row.categoryColor} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{row.title}</span>
                      <span className="block truncate text-[12px] text-muted">
                        {row.paidByName} · {formatRelativeDate(row.date)}
                      </span>
                    </span>
                    <Money
                      minor={row.amountMinor}
                      currency={currency}
                      className="text-sm font-semibold"
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {expenses.length > recent.length && (
            <p className="border-t border-line px-5 py-3 text-[12px] text-muted">
              Showing {recent.length} of {expenses.length}.{' '}
              <Link to="/expenses" className="font-semibold text-primary hover:underline">
                View all expenses
              </Link>
            </p>
          )}
        </Card>
      </div>
    </>
  );
}

function BalanceRow({
  row,
  currency,
  share,
  onClick,
}: {
  row: MemberBalance;
  currency: string;
  /** This member's |net| relative to the largest in the group, 0–1. */
  share: number;
  onClick: () => void;
}) {
  const tone = row.netMinor > 0 ? 'bg-success' : row.netMinor < 0 ? 'bg-danger' : 'bg-line';

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-elevated"
    >
      <Avatar name={row.memberName} id={row.memberId} dimmed={!row.isActive} />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-3">
          <span className="truncate text-sm font-semibold">{row.memberName}</span>
          <Money
            minor={row.netMinor}
            currency={currency}
            signed
            className="shrink-0 text-sm font-bold"
          />
        </span>
        <span className="mt-1.5 flex items-center gap-3">
          <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-elevated">
            <span
              className={`block h-full rounded-full ${tone}`}
              style={{ width: `${row.netMinor === 0 ? 0 : Math.max(4, share * 100)}%` }}
            />
          </span>
          <span className="hidden w-36 shrink-0 truncate text-right text-[12px] text-muted sm:block">
            {balancePhrase(row.netMinor, currency)}
          </span>
        </span>
      </span>
    </button>
  );
}
